import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/input/Input";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useState } from "react";

const TIMER_DURATION_SECONDS = 180;
const ROTATE_ICON = byPrefixAndName.fas["arrows-rotate-reverse"];

function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function PinTimer({ timeLeft, setTimeLeft, onTimeUp }) {
  useEffect(() => {
    if (timeLeft <= 0) {
      onTimeUp();
      return;
    }

    const timerId = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [timeLeft, setTimeLeft, onTimeUp]);

  const formattedTime = useMemo(() => formatTime(timeLeft), [timeLeft]);

  return <div className="text-[#DC2626]">{formattedTime}</div>;
}

export default function PhoneConfirmationModal({
  payload,
  onConfirm,
  onResendCode,
}) {
  const t = useTranslationDetector("savedCards");
  const [code, setCode] = useState("");
  const [remainingTime, setRemainingTime] = useState(TIMER_DURATION_SECONDS);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [requestError, setRequestError] = useState(false);

  const handleTimeUp = useCallback(() => {
    setIsTimeUp(true);
  }, []);

  const handleResendCode = useCallback(() => {
    onResendCode(payload);
    setRemainingTime(TIMER_DURATION_SECONDS);
    setIsTimeUp(false);
  }, [onResendCode, payload]);

  const handleConfirm = useCallback(async () => {
    const response = await onConfirm({ ...payload, OtpToken: code });

    if (!response) {
      setRequestError(true);
    }
  }, [onConfirm, payload, code]);

  const handleInputChange = useCallback(
    (e) => {
      if (requestError) {
        setRequestError(false);
      }

      setCode(e.target.value);
    },
    [requestError],
  );

  const isConfirmDisabled = isTimeUp || !code;
  const resendButtonClassName = `flex items-center gap-x-1 text-sm ${!isTimeUp ? "text-foreground-muted" : ""}`;

  return (
    <div className="flex flex-col gap-6">
      <div className="text-primary-muted-subtext text-center">
        {t("phoneConfirmationDescription", { phone: payload?.phoneNumber })}
      </div>

      <div className="flex flex-col gap-4">
        {requestError && (
          <p className="text-danger flex items-center gap-1 text-xs">
            <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
            <span>{t("phoneConfirmationError")}</span>
          </p>
        )}
        <Input value={code} onChange={handleInputChange} />

        <div className="flex justify-between">
          <PinTimer
            timeLeft={remainingTime}
            setTimeLeft={setRemainingTime}
            onTimeUp={handleTimeUp}
          />

          <button
            disabled={!isTimeUp}
            className={resendButtonClassName}
            onClick={handleResendCode}
          >
            <FontAwesomeIcon
              className="text-foreground-muted text-base"
              icon={ROTATE_ICON}
            />
            {t("resendCode")}
          </button>
        </div>

        <Button disabled={isConfirmDisabled} onClick={handleConfirm}>
          {t("submitCode")}
        </Button>
      </div>
    </div>
  );
}
